import { boundMethod } from 'autobind-decorator';
import { AsyncStatus, Tag } from 'lib-shared-legacy-types';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import { ajaxSend } from 'api/api';

import {
  activateTag,
  ActiveState,
  createTag,
  deactivateTag,
  getTags,
  getTagsByName,
  removeTag,
  TagsGetItemResponse,
  TagsGetItemsResponse,
} from './api/tags';

const DEFAULT_FETCH_THROTTLE = 300;

export interface WithTagsInjectedProps {
  tags: {
    /** Loaded tags of current page */
    items: Array<Tag>;

    /** AsyncStatus of get tags api */
    getItemsAsyncStatus: AsyncStatus;

    /** AsyncStatus of create tag api */
    createAsyncStatus: AsyncStatus;

    /** AsyncStatus of remove tag api */
    removeAsyncStatus: AsyncStatus;

    /** The search term used in get tags api */
    searchTerm: string;

    /** The sorting option used in get tags api */
    sortBy: string;

    /** The filtering option used in get tags api */
    filterBy: ActiveState;

    /** Whether there is next page or not */
    hasNextPage: boolean;

    /** Whether there is previous page or not */
    hasPreviousPage: boolean;

    /** Action to get tags. It will use the current search term to filter tags. */
    getItems: () => Promise<TagsGetItemsResponse>;

    /** Action to get tags from the ids provided. */
    getItemsById: (tagIds: Array<string>) => Promise<TagsGetItemsResponse>;

    /**
     * Handle search term change.
     *
     * @param {string} searchTerm - The search term
     * @param {number} [fetchThrottle=300] - If > 0 it will re-fetch tags after
     *   given milliseconds. Default is `300`
     */
    searchTermChange: (
      searchTerm?: string,
      fetchThrottle?: number,
    ) => Promise<TagsGetItemsResponse | void>;

    /**
     * Handle sorting change.
     *
     * @param {string} sortBy - Column to sort the list by
     * @param {string} direction - "asc" or "desc", if missing the default is
     *   "asc" on the backend
     */
    sortByChange: (
      sortBy?: string,
      direction?: string,
    ) => Promise<TagsGetItemsResponse> | void;

    /** Get tags of the next page */
    nextPage: () => Promise<TagsGetItemsResponse>;

    /** Get tags of the previous page */
    previousPage: () => Promise<TagsGetItemsResponse>;

    /**
     * Create a new tag
     *
     * @param {Pick<Tag, 'name' | 'color'>} - The tag to be created
     */
    create: (tag: Pick<Tag, 'name' | 'color'>) => Promise<TagsGetItemResponse>;

    /**
     * Remove a tag from database
     *
     * @param {string} tagId - Id of the tag to be removed. Currently it is the
     *   name that works as id
     */
    remove: (tagId: string) => Promise<TagsGetItemResponse>;

    /**
     * Activate/deactivate a tag.
     *
     * @param {string} tagId - Id of the tag to activate/deactivate.
     */
    toggleActivation: (tag: Tag) => Promise<TagsGetItemResponse>;

    /**
     * Handle filtering change
     *
     * @param {ActiveState} filterBy - The filter to apply
     */
    filterByChange: (filterBy: ActiveState) => void;
  };
}

type WithTagsNotInjectedProps<T> = Omit<T, keyof WithTagsInjectedProps>;

type WithTagsProps<T> = WithTagsNotInjectedProps<T>;

interface WithTagsState {
  searchTerm: string;
  sortBy: string;
  items: Array<Tag>;
  nextPagePath: string | null;
  previousPagePath: string | null;
  filterBy: ActiveState;
  getItemsAsyncStatus: AsyncStatus;
  createAsyncStatus: AsyncStatus;
  removeAsyncStatus: AsyncStatus;
  activationStatus: AsyncStatus;
}

interface WithTagsSettings {
  pageSize?: number;
}

export const withTags = <T extends WithTagsInjectedProps>(
  Component: ComponentType<T>,
  settings: WithTagsSettings = {},
) => {
  class WithTags extends PureComponent<WithTagsProps<T>, WithTagsState> {
    timeout: ReturnType<typeof setTimeout> | null;

    constructor(props: WithTagsProps<T>) {
      super(props);

      this.timeout = null;
      this.state = {
        items: [],
        searchTerm: '',
        sortBy: 'usage_count:desc',
        filterBy: null,
        nextPagePath: null,
        previousPagePath: null,
        getItemsAsyncStatus: 'succeeded',
        createAsyncStatus: 'succeeded',
        removeAsyncStatus: 'succeeded',
        activationStatus: 'succeeded',
      };
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        isEqual,
      ).bind(this); // We have to bind it like this. Using @boundMethod won't work
    }

    componentWillUnmount() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
    }

    @boundMethod
    getItems(): Promise<TagsGetItemsResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return getTags({
        search: this.state.searchTerm,
        limit: settings.pageSize,
        sort_by: !this.state.searchTerm ? this.state.sortBy : '', // We don't support sort and search at the same time
        active_state: this.state.filterBy,
      })
        .then(this.handleQueryResolve)
        .catch(this.handleQueryReject);
    }

    @boundMethod
    getItemsWithPath(path: string): Promise<TagsGetItemsResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return ajaxSend<TagsGetItemsResponse>({
        path,
      })
        .then(this.handleQueryResolve)
        .catch(this.handleQueryReject);
    }

    @boundMethod
    getItemsById(tagIds: Array<string>): Promise<TagsGetItemsResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return getTagsByName(tagIds)
        .then(this.handleQueryResolve)
        .catch(this.handleQueryReject);
    }

    @boundMethod
    nextPage(): Promise<TagsGetItemsResponse> | never {
      const { nextPagePath } = this.state;
      if (nextPagePath) {
        return this.getItemsWithPath(nextPagePath);
      }
      throw new Error('Next page does not exist');
    }

    @boundMethod
    previousPage(): Promise<TagsGetItemsResponse> | never {
      const { previousPagePath } = this.state;
      if (previousPagePath) {
        return this.getItemsWithPath(previousPagePath);
      }
      throw new Error('Previous page does not exist');
    }

    @boundMethod
    create(tag: Pick<Tag, 'name' | 'color'>) {
      this.setState({ createAsyncStatus: 'loading' });
      return createTag(tag)
        .then((response) => {
          this.setState({ createAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ createAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    remove(tagId: string) {
      this.setState({ removeAsyncStatus: 'loading' });
      return removeTag(tagId)
        .then((response) => {
          this.setState({ removeAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ removeAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    toggleActivation(tag: Tag): Promise<TagsGetItemResponse> {
      this.setState({ activationStatus: 'loading' });

      const tagActivationFunction = tag.is_deactivated
        ? activateTag
        : deactivateTag;

      return tagActivationFunction(tag.id || tag.name).then((response) => {
        this.setState(({ items }) => {
          const newItems = [...items];
          const tagIndex = newItems.findIndex(
            (tag) => tag.id === response.data.id,
          );
          if (tagIndex > -1) {
            newItems[tagIndex] = response.data;
          }
          return {
            items: newItems,
          };
        });
        return response;
      });
    }

    @boundMethod
    handleSearchTermChange(
      searchTerm = '',
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ): Promise<TagsGetItemsResponse | void> {
      return new Promise((resolve, reject) => {
        this.setState({ searchTerm }, () => {
          if (fetchThrottle >= 0) {
            if (this.timeout) {
              clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
              resolve(this.getItems());
            }, fetchThrottle);
          } else {
            resolve();
          }
        });
      });
    }

    @boundMethod
    handleSortByChange(
      sortBy = '',
      direction = 'asc',
    ): Promise<TagsGetItemsResponse> | never {
      return new Promise((resolve, reject) => {
        this.setState(
          {
            sortBy: `${sortBy}:${direction}`,
          },
          () => {
            resolve(this.getItems());
          },
        );
      });
    }

    @boundMethod
    handleQueryResolve(response: TagsGetItemsResponse) {
      this.setState({
        getItemsAsyncStatus: 'succeeded',
        items: response.data.tags,
        nextPagePath: response.meta.next_page,
        previousPagePath: response.meta.prev_page,
      });

      return response;
    }

    @boundMethod
    handleQueryReject(error) {
      this.setState({
        getItemsAsyncStatus: 'failed',
      });

      return error;
    }

    @boundMethod
    handleFilterByChange(filterBy: ActiveState) {
      return new Promise((resolve) => {
        this.setState(
          {
            filterBy,
          },
          () => {
            resolve(this.getItems());
          },
        );
      });
    }

    @boundMethod
    getInjectedProps(
      props: Readonly<WithTagsProps<T>>,
      state: Readonly<WithTagsState>,
    ): WithTagsInjectedProps['tags'] {
      const {
        items,
        searchTerm,
        sortBy,
        filterBy,
        nextPagePath,
        previousPagePath,
        getItemsAsyncStatus,
        createAsyncStatus,
        removeAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        searchTerm,
        sortBy,
        filterBy,
        items,
        getItemsAsyncStatus,
        createAsyncStatus,
        removeAsyncStatus,
        nextPagePath,
        previousPagePath,
      );
    }

    memoizedGetInjectedProps(
      searchTerm: Readonly<WithTagsState>['searchTerm'],
      sortBy: Readonly<WithTagsState>['sortBy'],
      filterBy: Readonly<WithTagsState>['filterBy'],
      items: Readonly<WithTagsState>['items'],
      getItemsAsyncStatus: Readonly<WithTagsState>['getItemsAsyncStatus'],
      createAsyncStatus: Readonly<WithTagsState>['createAsyncStatus'],
      removeAsyncStatus: Readonly<WithTagsState>['removeAsyncStatus'],
      nextPagePath: Readonly<WithTagsState>['nextPagePath'],
      previousPagePath: Readonly<WithTagsState>['previousPagePath'],
    ): WithTagsInjectedProps['tags'] {
      return {
        searchTerm: searchTerm,
        sortBy: sortBy,
        filterBy,
        items: items,
        getItemsAsyncStatus: getItemsAsyncStatus,
        createAsyncStatus: createAsyncStatus,
        removeAsyncStatus: removeAsyncStatus,
        hasNextPage: nextPagePath !== null,
        hasPreviousPage: previousPagePath !== null,
        getItems: this.getItems,
        getItemsById: this.getItemsById,
        searchTermChange: this.handleSearchTermChange,
        sortByChange: this.handleSortByChange,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
        create: this.create,
        remove: this.remove,
        toggleActivation: this.toggleActivation,
        filterByChange: this.handleFilterByChange,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as T)}
          tags={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithTags;
};
